import React, { useRef, useEffect, useState } from 'react';

/**
 * Markdown Editor with support for overlay widgets at cursor position
 * Uses absolute positioning overlay for edit UI
 */
const MarkdownEditor = ({
  documentContent,
  setDocumentContent,
  handleTextSelection,
  textAreaRef,
  scrollTop,
  onScroll,
  inlineWidget, // { component: ReactNode, position: number }
  onWidgetClose,
  disableScroll = false // New prop to lock scrolling
}) => {
  const containerRef = useRef(null);
  const [widgetTop, setWidgetTop] = useState(0);

  // Calculate widget position based on cursor
  useEffect(() => {
    if (inlineWidget && inlineWidget.position !== undefined && inlineWidget.component && textAreaRef.current) {
      const textarea = textAreaRef.current;
      const text = documentContent.substring(0, inlineWidget.position);
      const lines = text.split('\n');
      const lineNumber = lines.length;

      // Approximate line height
      const lineHeight = 28; // 7 * 4 (leading-7 = 1.75rem = 28px)
      const paddingTop = 24; // p-6 = 24px

      // Calculate initial position
      let calculatedTop = paddingTop + (lineNumber * lineHeight);

      // Ensure widget stays within viewport
      setTimeout(() => {
        if (containerRef.current) {
          const widget = containerRef.current.querySelector('.inline-widget-overlay');
          if (widget) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const widgetHeight = widget.offsetHeight;
            const containerHeight = containerRect.height;

            // If widget would overflow bottom, adjust position
            if (calculatedTop + widgetHeight > containerHeight) {
              calculatedTop = Math.max(paddingTop, containerHeight - widgetHeight - 20);
            }

            setWidgetTop(calculatedTop);

            // Scroll widget into view smoothly
            const rect = widget.getBoundingClientRect();
            if (rect.bottom > containerRect.bottom || rect.top < containerRect.top) {
              widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      }, 50);

      // Set initial position immediately to prevent flash
      setWidgetTop(calculatedTop);
    }
  }, [inlineWidget, documentContent, textAreaRef]);

  const handleTextareaScroll = (e) => {
    // Prevent scroll if disabled (when pending edit exists)
    if (disableScroll) {
      e.preventDefault();
      return;
    }

    if (!onScroll) return;
    const element = e.target;
    const scrollPercentage = element.scrollTop / (element.scrollHeight - element.clientHeight) || 0;
    onScroll(scrollPercentage);
  };

  // Sync scroll position from preview to editor
  useEffect(() => {
    // Don't sync scroll if scroll is disabled
    if (disableScroll) return;

    if (textAreaRef.current && scrollTop !== undefined) {
      const element = textAreaRef.current;
      const maxScroll = Math.max(0, element.scrollHeight - element.clientHeight);
      const targetScrollTop = scrollTop * maxScroll;

      // Only update if the difference is significant
      if (Math.abs(element.scrollTop - targetScrollTop) > 5) {
        element.scrollTop = targetScrollTop;
      }
    }
  }, [scrollTop, textAreaRef, disableScroll]);

  return (
    <div ref={containerRef} className="w-full h-full relative bg-white overflow-hidden">
      <div className="absolute inset-0 p-6">
        {/* Always show textarea */}
        <textarea
          ref={textAreaRef}
          value={documentContent}
          onChange={(e) => setDocumentContent(e.target.value)}
          onSelect={handleTextSelection}
          onKeyUp={handleTextSelection}
          onScroll={handleTextareaScroll}
          className={`w-full h-full resize-none focus:outline-none font-mono text-sm leading-7 bg-transparent border-0 text-gray-900 block ${disableScroll ? 'overflow-hidden' : ''}`}
          placeholder="✍️  Start crafting your document...

Transform your ideas into beautifully formatted content with markdown.

🚀  Quick Tips:
   • Use # for headings and **bold** for emphasis
   • Add $math equations$ for formulas
   • Create lists with - or 1. for structure
   • Select text and use AI tools to edit

📖  Need more help? Click the help icon (?) in the toolbar for the complete markdown guide.

💡  Pro tip: Select any text you write and use the AI tools to expand, refine, or reformat your content!"
          spellCheck={false}
          style={inlineWidget ? { opacity: 0.3 } : {}}
        />

        {/* Inline widget overlay */}
        {inlineWidget && inlineWidget.component && (
          <div
            className="inline-widget-overlay absolute left-4 right-4 sm:left-6 sm:right-6 z-10 max-w-4xl mx-auto"
            style={{ top: `${widgetTop}px` }}
          >
            {inlineWidget.component}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarkdownEditor;
